import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend
} from "recharts";
import { TrendingUp, Database, Users, FileText } from "lucide-react";
import "./Dashboard.css";

const safeVal = (field, fallback = "No data") => {
  if (field === null || field === undefined) return fallback;
  if (typeof field === "object") {
    if ("count" in field) return field.count ?? fallback;
    const numericEntry = Object.values(field).find((v) => typeof v === "number");
    return numericEntry !== undefined ? numericEntry : fallback;
  }
  return field;
};

const safeTotalAmount = (field) => {
  if (!field || typeof field !== "object") return Number(field) || 0;
  if ("total_amount" in field) return Number(field.total_amount) || 0;
  if ("total_price"  in field) return Number(field.total_price)  || 0;
  return 0;
};

const fmt = (val) =>
  Number(val || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [communityData, setCommunityData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");

  const BASE_URL = " https://sadaatagrofarms.com";

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    fetch(" https://sadaatagrofarms.com/api/dashboard/", {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => res.json())
      .then((result) => setData(result))
      .catch((err) => console.error("Dashboard API Error:", err));

    fetch(" https://sadaatagrofarms.com/api/community/stats/", {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => res.json())
      .then((result) => setCommunityData(result))
      .catch((err) => console.error("Community API Error:", err));
  }, []);

  if (!data || !communityData)
    return <div className="dashboard">Loading Dashboard...</div>;

  const budgetChartData = [
    { name: "Monthly",  value: safeTotalAmount(data.monthly_budgets)  },
    { name: "Seasonal", value: safeTotalAmount(data.seasonal_budgets) },
    { name: "Combined", value: safeTotalAmount(data.combined_budgets) }
  ];

  const financialChartData = [
    { name: "Expenses",     value: safeTotalAmount(data.operational_expense_records) },
    { name: "Crop Income",  value: safeTotalAmount(data.crop_income_records)          },
    { name: "Other Profit", value: safeTotalAmount(data.other_profit_records)         }
  ];

  const hasBudgetData    = budgetChartData.some((d) => d.value > 0);
  const hasFinancialData = financialChartData.some((d) => d.value > 0);

  const COLORS = ["#387eff", "#00c49f", "#ff9800"];

  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const posts =
    modalType === "liked"
      ? communityData.top_5_most_liked   || []
      : communityData.top_5_most_commented || [];

  const mbi = data.monthly_budget_items || {};

  return (
    <div className="dashboard">
      <h1 className="dashboard-title">Farm Management Dashboard</h1>

      {/* ===============================
         TOP STAT CARDS
      ===============================*/}
      <div className="stats-grid">
        <div className="stat-card">
          <Database size={28} />
          <h3>{safeVal(data.total_farms)}</h3>
          <p>Total Farms</p>
        </div>

        <div className="stat-card">
          <Users size={28} />
          <h3>{safeVal(data.total_farmers)}</h3>
          <p>Total Farmers</p>
        </div>

        <div className="stat-card">
          <TrendingUp size={28} />
          <h3>{safeVal(data.posts_count)}</h3>
          <p>Total Posts</p>
        </div>

        <div className="stat-card">
          <FileText size={28} />
          <h3>{safeVal(mbi, "0")}</h3>
          <p>Budget Items</p>
          {mbi.total_price != null && (
            <small style={{ fontSize: 11, color: "#6b8070", marginTop: 2, display: "block" }}>
              Rs {fmt(mbi.total_price)}
            </small>
          )}
        </div>
      </div>

      {/* ===============================
         BUDGET CHART
      ===============================*/}
      <div className="card">
        <h2>Budget Distribution (Rs)</h2>
        {hasBudgetData ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={budgetChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => `Rs ${fmt(v)}`} />
              <Tooltip formatter={(v) => [`Rs ${fmt(v)}`, "Amount"]} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {budgetChartData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="no-data">No data available</p>
        )}
      </div>

      {/* ===============================
         FINANCIAL PIE CHART
      ===============================*/}
      <div className="card">
        <h2>Financial Overview (Rs)</h2>
        {hasFinancialData ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={financialChartData}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                label={({ name, value }) => `${name}: Rs ${fmt(value)}`}
              >
                {financialChartData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [`Rs ${fmt(v)}`, "Amount"]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="no-data">No data available</p>
        )}
      </div>

      {/* ===============================
         COMMUNITY PREVIEW SECTION
      ===============================*/}
      <div className="stats-grid">
        <div className="highlight-card">
          <h3>🔥 Top 5 Most Liked</h3>
          <div className="big-number">
            {(communityData.top_5_most_liked || []).length || "No data"}
          </div>
          <button onClick={() => openModal("liked")}>Preview</button>
        </div>

        <div className="highlight-card">
          <h3>💬 Top 5 Most Commented</h3>
          <div className="big-number">
            {(communityData.top_5_most_commented || []).length || "No data"}
          </div>
          <button onClick={() => openModal("commented")}>Preview</button>
        </div>
      </div>

      {/* ===============================
         MODAL
      ===============================*/}
      {modalOpen && (
        <div className="modal-overlay1" onClick={() => setModalOpen(false)}>
          <div
            className="modal-content1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {modalType === "liked"
                  ? "🔥 Top 5 Most Liked Posts"
                  : "💬 Top 5 Most Commented Posts"}
              </h2>
              <button onClick={() => setModalOpen(false)}>✖</button>
            </div>

            {posts.length === 0 ? (
              <p className="no-data">No data available</p>
            ) : (
              <div className="posts-grid">
                {posts.map((post) => (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <strong>@{post.username || "Unknown"}</strong>
                      <span>
                        {post.created_at
                          ? new Date(post.created_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>

                    <p>{post.text || <em>No content</em>}</p>

                    {post.image && (
                      <img
                        src={BASE_URL + post.image}
                        alt=""
                        className="post-media"
                      />
                    )}

                    {post.video && (
                      <video
                        controls
                        src={BASE_URL + post.video}
                        className="post-media"
                      />
                    )}

                    <div className="post-stats">
                      ❤️ {safeVal(post.likes_count, 0)}
                      <span>💬 {safeVal(post.comments_count, 0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;